import React from 'react';
import { useTranslation } from 'react-i18next';

const API_URL = import.meta.env.VITE_API_URL;

export function HomePreview({ formData }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language.startsWith('de') ? 'de' : 'en';

  // Helper to get the right field or fallback
  const getField = (base) => {
    // Special mapping for about section
    if (base === 'about_title') {
      return lang === 'de' ? formData.title_de : formData.title_en;
    }
    if (base === 'description') {
      return lang === 'de' ? formData.description_de : formData.description_en;
    }
    // For other fields, use the direct mapping
    const field = `${base}_${lang}`;
    return formData[field] || '';
  };

  // Helper to get image URL
  const getImageUrl = (image) => {
    if (!image) return null;
    if (typeof image === 'string') {
      return image.startsWith('http')
        ? image
        : `${API_URL.replace(/\/$/, '')}/${image.replace(/^\//, '')}`;
    }
    return URL.createObjectURL(image);
  };

  return (
    <div className="mt-12 border rounded-2xl shadow-lg bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-800 transition-colors duration-300 overflow-hidden">
      <div className="px-4 pt-8 pb-2">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          {t('homepreview.section_title', 'Live-Vorschau der Home-Seite')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {t('homepreview.section_desc', 'So sieht die Startseite aktuell für Besucher aus.')}
        </p>
      </div>
      {/* Navbar Preview */}
      <nav className="w-full bg-gray-100 dark:bg-zinc-900 text-gray-900 dark:text-white px-4 md:px-12 py-4 flex flex-col md:flex-row items-center md:items-end gap-2 md:gap-6 border-b border-gray-200 dark:border-zinc-800">
        <div className="flex-1 flex flex-col md:flex-row md:items-end gap-2">
          <span className="text-3xl md:text-4xl font-serif font-bold leading-none">PeART</span>
          <span className="text-base md:text-lg font-light text-gray-500 dark:text-gray-300 md:ml-4 md:mb-1">
            {getField('nav_subtitle') || 'Zeitgenössische Kunst & Malerei – Atelier PeART'}
          </span>
        </div>
        {/* Simulierte Menü-Buttons (optional) */}
        <div className="hidden md:flex gap-4 text-gray-400 dark:text-gray-300 text-base font-medium">
          <span className="opacity-60">Home</span>
          <span className="opacity-60">Galerie</span>
          <span className="opacity-60">Über</span>
          <span className="opacity-60">Kontakt</span>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative w-full min-h-[200px] md:min-h-[320px] flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-zinc-800 transition-colors duration-300">
        <img
          src={getImageUrl(formData.hero_image) || '/hero-image.jpeg'}
          alt="Hero Preview"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-black/70 to-black/50 z-10" />
        <div className="relative z-20 w-full max-w-2xl mx-auto text-center px-4 py-8">
          <h1 className="text-4xl md:text-7xl font-bold font-serif mb-6 text-white drop-shadow-lg">{getField('title') || 'PeART'}</h1>
          <p className="text-xl md:text-2xl lg:text-3xl mb-8 max-w-3xl mx-auto text-white drop-shadow">{getField('hero_subtitle') || 'Willkommen auf der Vorschau!'}</p>
          <button
            className="inline-block bg-white dark:bg-zinc-900 text-black dark:text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors border border-gray-200 dark:border-zinc-700 shadow"
            disabled
          >
            Galerie ansehen
          </button>
        </div>
      </div>

      {/* About Section */}
      <section className="py-12 px-4 md:px-12 lg:px-24 bg-gray-50 dark:bg-zinc-900 border-t border-gray-100 dark:border-zinc-800 transition-colors duration-300 w-full flex justify-center">
        <div className="w-full max-w-7xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8 text-center text-gray-900 dark:text-white">
            {getField('about_title') || 'Über PeART'}
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-gray-700 dark:text-gray-300 text-lg">
              <p className="whitespace-pre-line">{getField('description') || 'Beschreibung...'}</p>
              <p className="whitespace-pre-line">{lang === 'de' ? formData.about_description2_de : formData.about_description2_en || ''}</p>
            </div>
            <div className="flex items-center justify-center">
              <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-lg border border-gray-200 dark:border-zinc-700 p-2 max-w-xs md:max-w-sm lg:max-w-md w-full">
                <img
                  src={getImageUrl(formData.about_image) || '/about-image.jpeg'}
                  alt="About Preview"
                  className="w-full h-auto rounded-lg object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
} 